import { type AtticActivityFilterInput, AtticActivityKindEnum } from "@dashboard/graphql";
import { type Filters, type Pagination } from "@dashboard/types";
import { withQuery } from "@dashboard/utils/urls";

export const atticActivitySectionUrlName = "/attic/activity-log";

export const atticActivityListPath = `${atticActivitySectionUrlName}/`;

export enum AtticActivityListUrlFiltersEnum {
  kind = "kind",
  query = "query",
  subject = "subject",
  createdFrom = "createdFrom",
  createdTo = "createdTo",
}
export type AtticActivityListUrlFilters = Filters<AtticActivityListUrlFiltersEnum>;
export type AtticActivityListUrlQueryParams = Pagination & AtticActivityListUrlFilters;

/**
 * The list with its filters and cursor in the query string, the stock shape
 * (`?subject=product:12` opens the log narrowed to one record). No bare `?`
 * when there is nothing to say.
 */
export const atticActivityListUrl = (params?: AtticActivityListUrlQueryParams) =>
  withQuery(atticActivityListPath, params);

const KINDS = new Set<string>(Object.values(AtticActivityKindEnum));

/** The kind in the URL, or undefined for anything that is not one of the API's. */
export const parseAtticActivityKind = (kind: string | undefined) =>
  kind && KINDS.has(kind) ? (kind as AtticActivityKindEnum) : undefined;

/** `YYYY-MM-DD`, as the date inputs write it; anything else is ignored. */
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const isDay = (value: string | undefined): value is string => {
  if (!value || !DAY.test(value)) {
    return false;
  }

  // A day that rolls over (2026-02-30 parses as March 2) is not a day.
  const parsed = new Date(`${value}T00:00:00`);
  const [year, month, day] = value.split("-").map(Number);

  return (
    parsed.getFullYear() === year && parsed.getMonth() + 1 === month && parsed.getDate() === day
  );
};

/** A `YYYY-MM-DD` day's bound in the viewer's own timezone, as an instant. */
const dayBound = (day: string, end: boolean) =>
  new Date(`${day}T${end ? "23:59:59.999" : "00:00:00.000"}`).toISOString();

/**
 * The API's filter for the URL's params. Days bound the range in the
 * viewer's own timezone, whole days inclusive, sent as instants.
 */
export const getAtticActivityFilter = (
  params: AtticActivityListUrlFilters,
): AtticActivityFilterInput => {
  const kind = parseAtticActivityKind(params.kind);
  const from = isDay(params.createdFrom) ? params.createdFrom : undefined;
  const to = isDay(params.createdTo) ? params.createdTo : undefined;

  return {
    ...(params.query ? { search: params.query } : {}),
    ...(kind ? { kind } : {}),
    ...(params.subject ? { subject: params.subject } : {}),
    ...(from || to
      ? {
          createdAt: {
            ...(from ? { gte: dayBound(from, false) } : {}),
            ...(to ? { lte: dayBound(to, true) } : {}),
          },
        }
      : {}),
  };
};
